import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from '../../../../icons';
import { TYPE_COLOR_MAP, type CalendarEventType } from '../../../../api/calendar';
import { getDateLocale } from '../../../../lib/base-i18n';
import { useAuthStore } from '../../../../store/authStore';
import { useMenuGate } from '../../../../lib/menu-gate';
import { WidgetCard } from '../WidgetCard';
import { useWidgetActions } from '../widget-context';
import { useWidgetChrome } from '../widget-chrome';
import { useMonthEvents } from '../widget-queries';
// 대시보드 시안 v2 V3a (2026-09-16) — 보이는 항목 · 선택지를 읽는 판. 깃발이 꺼져 있으면 아래 옛 판 그대로.
import { DASHBOARD_V2_ENABLED } from '../../../../lib/layout-shell';
import { ScheduleDetailWidgetV2 } from './v2/ScheduleDetailWidgetV2';

function typeMeta(type: string) {
  return TYPE_COLOR_MAP[type as CalendarEventType] ?? { label: '기타', color: 'var(--text-muted)' };
}

const DOW = ['일', '월', '화', '수', '목', '금', '토'] as const;

/** 그날 칸 첫 열의 시각. 자료에 있는 `startAt` 만 쓴다 — 없는 칸을 지어내지 않는다. */
function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 캘린더 위젯 (#2a) — 월 그리드. 일정 있는 날엔 점, 오늘은 채움. */
export function CalendarMiniWidget() {
  const { go } = useWidgetActions();
  const today = new Date();
  const [cursor, setCursor] = useState(() => ({ y: today.getFullYear(), m: today.getMonth() }));
  // 관리 콘솔에서 캘린더 메뉴를 끄면 서버가 일정 조회를 막는다 → 요청 접고 안내만(2026-08-11).
  const gate = useMenuGate('calendar', '캘린더');
  const { data, loading, error } = useMonthEvents(cursor.y, cursor.m, gate.enabled);

  /**
   * 「날짜를 누르면」 (2026-09-16 · 사용자 요청) — 기본은 **그 아래에 펴기**.
   * ★★ `'navigate'` 면 **종전 그대로** 캘린더 화면으로 간다(이동하던 길을 없애지 않는다).
   * ★ 서버를 새로 부르지 않는다 — 점을 찍으려고 이미 받아 둔 그 달 일정을 그대로 고른다.
   */
  const dayClick = useWidgetChrome().settings.dayClick ?? 'expand';
  const [openDay, setOpenDay] = useState<number | null>(null);
  const dayEvents = useMemo(() => {
    if (openDay == null) return [];
    return (data ?? [])
      .filter((e) => {
        const d = new Date(e.startAt);
        return d.getFullYear() === cursor.y && d.getMonth() === cursor.m && d.getDate() === openDay;
      })
      .sort((a, b) => (a.startAt < b.startAt ? -1 : 1));
  }, [data, cursor.y, cursor.m, openDay]);

  const daysWithEvents = useMemo(() => {
    const set = new Set<number>();
    for (const e of data ?? []) {
      const d = new Date(e.startAt);
      if (d.getFullYear() === cursor.y && d.getMonth() === cursor.m) set.add(d.getDate());
    }
    return set;
  }, [data, cursor.y, cursor.m]);

  const firstDow = new Date(cursor.y, cursor.m, 1).getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const isThisMonth = today.getFullYear() === cursor.y && today.getMonth() === cursor.m;

  const shift = (delta: number) => {
    setOpenDay(null);   // 달을 넘기면 펼친 칸을 닫는다 — 그 달에 없는 날짜의 칸이 남지 않게
    setCursor(({ y, m }) => {
      const next = new Date(y, m + delta, 1);
      return { y: next.getFullYear(), m: next.getMonth() };
    });
  };

  return (
    <WidgetCard title="캘린더" loading={loading} error={error} menuOff={gate.enabled ? null : gate.label}>
      <div className="dw-cal">
        <div className="dw-cal__nav">
          <button type="button" className="dw-icon-btn" onClick={() => shift(-1)} aria-label="이전 달">
            <ChevronLeft size={15} aria-hidden />
          </button>
          <button type="button" className="dw-cal__month" onClick={() => go('/calendar')}>
            {cursor.y}년 {cursor.m + 1}월
          </button>
          <button type="button" className="dw-icon-btn" onClick={() => shift(1)} aria-label="다음 달">
            <ChevronRight size={15} aria-hidden />
          </button>
        </div>
        <div className="dw-cal__grid">
          {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
            <div key={d} className={`dw-cal__dow${i === 0 ? ' dw-cal__dow--sun' : i === 6 ? ' dw-cal__dow--sat' : ''}`}>{d}</div>
          ))}
          {Array.from({ length: firstDow }, (_, i) => <div key={`pad-${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dow = (firstDow + i) % 7;
            const isToday = isThisMonth && day === today.getDate();
            const cls = [
              'dw-cal__day',
              dow === 0 ? 'dw-cal__day--sun' : dow === 6 ? 'dw-cal__day--sat' : '',
              isToday ? 'dw-cal__day--today' : '',
            ].filter(Boolean).join(' ');
            return (
              <button
                type="button" key={day} className={cls}
                aria-expanded={dayClick === 'expand' ? openDay === day : undefined}
                onClick={() => {
                  if (dayClick === 'navigate') { go('/calendar'); return; }   // 종전 동작 그대로
                  if (dayClick === 'none') return;
                  setOpenDay((cur) => (cur === day ? null : day));
                }}
              >
                {day}
                {daysWithEvents.has(day) && <span className="dw-cal__dot" aria-hidden />}
              </button>
            );
          })}
        </div>

        {/* 그날 칸 — 달력 «아래»에 더한다(달력 렌더 경로는 그대로다). */}
        {dayClick === 'expand' && openDay != null && (
          <div className="dw-calday">
            <div className="dw-calday__h">
              {cursor.m + 1}월 {openDay}일 ({DOW[new Date(cursor.y, cursor.m, openDay).getDay()]})
            </div>
            {dayEvents.length === 0
              ? <div className="dw-calday__empty">이 날 일정이 없습니다</div>
              : dayEvents.slice(0, 3).map((e) => (
                <div className="dw-calday__row" key={e.id}>
                  <span className="dw-calday__t">{e.isAllDay ? '종일' : hhmm(e.startAt)}</span>
                  <span className="dw-calday__n">{e.title}</span>
                  {e.location && <span className="dw-calday__loc">{e.location}</span>}
                </div>
              ))}
            <button type="button" className="dw-calday__more" onClick={() => go('/calendar')}>
              캘린더에서 보기 →
            </button>
          </div>
        )}
      </div>
    </WidgetCard>
  );
}

type ScopeKey = 'company' | 'mine' | 'team';

const SCOPES: Array<{ key: ScopeKey; label: string }> = [
  { key: 'company', label: '회사 주요일정' },
  { key: 'mine', label: '내 일정' },
  { key: 'team', label: '팀원 일정' },
];

/**
 * 일정 상세 위젯 (#2a) — 오늘 일정을 시간대순으로.
 *
 * 세그먼트 매핑(백엔드에 '회사/팀원' 구분 필드가 없어 조회 가능한 값으로 정의):
 *   회사 주요일정 = 교육·기념일 타입(전사 성격) / 내 일정 = 내가 만든 일정 / 팀원 일정 = 남이 만든 일정.
 */
export function ScheduleDetailWidget() {
  return DASHBOARD_V2_ENABLED ? <ScheduleDetailWidgetV2 /> : <ScheduleDetailWidgetLegacy />;
}

function ScheduleDetailWidgetLegacy() {
  const { go } = useWidgetActions();
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const [scope, setScope] = useState<ScopeKey>('mine');
  const today = new Date();
  const gate = useMenuGate('calendar', '캘린더');
  const { data, loading, error } = useMonthEvents(today.getFullYear(), today.getMonth(), gate.enabled);

  const events = useMemo(() => {
    const d0 = new Date(today); d0.setHours(0, 0, 0, 0);
    const d1 = new Date(d0); d1.setDate(d0.getDate() + 1);
    return (data ?? []).filter((e) => {
      const t = new Date(e.startAt).getTime();
      if (t < d0.getTime() || t >= d1.getTime()) return false;
      if (scope === 'company') return e.type === 'training' || e.type === 'anniversary';
      if (scope === 'mine') return e.creatorId === myId;
      return e.creatorId !== myId;
    });
  }, [data, scope, myId]);

  const dateLabel = `${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;

  return (
    <WidgetCard
      title="일정 상세"
      tail={dateLabel}
      loading={loading}
      error={error}
      menuOff={gate.enabled ? null : gate.label}
      flush
    >
      <div className="dw-seg">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`dw-seg__btn${scope === s.key ? ' is-active' : ''}`}
            onClick={() => setScope(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>
      {events.length === 0
        ? <div className="dw-card__state">오늘 일정이 없습니다</div>
        : (
          <div className="dw-rows">
            {events.slice(0, 5).map((e) => {
              const meta = typeMeta(e.type);
              return (
                <button type="button" key={e.id} className="dw-ev" onClick={() => go('/calendar')}>
                  <span className="dw-ev__time">
                    {e.isAllDay ? '종일' : new Date(e.startAt).toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                  <span className="dw-ev__bar" style={{ background: meta.color }} aria-hidden />
                  <span className="dw-ev__main">
                    <span className="dw-ev__t" title={e.title}>{e.title}</span>
                    <span className="dw-ev__s">{[meta.label, e.location].filter(Boolean).join(' · ')}</span>
                  </span>
                </button>
              );
            })}
            {events.length > 5 && <div className="dw-more">+{events.length - 5}건 더</div>}
          </div>
        )}
    </WidgetCard>
  );
}
